import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../services/permission.service';
import { MatPaginator } from '@angular/material/paginator';
import { OverviewDataSource } from '../../../overview/overview-data-source';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { EMPTY, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SelectedCustomerService } from '../../services/selected-customer.service';
import { OverlayDialogHelper } from '../../utils/overlay-dialog.helper';
import moment from 'moment';
import { ConfigitQuote } from '../../services/quote.service';
import {
    PriceSummaryDialogComponent,
    PriceSummaryDialogData,
} from './price-summary-dialog/price-summary-dialog.component';
import { DOCUMENT } from '@angular/common';
import { openQuoteCreationDialog } from '../quote-creation-dialog/quote-creation-dialog.component';
import { OverviewDataService } from '../../../overview/overview.data.service';
import { openQuoteDeleteDialog } from '../quote-delete-dialog/quote-delete-dialog.component';
import { ImageDialogComponent, ImageDialogData } from '../templates-table/image-dialog/image-dialog.component';
import { AppService } from '../../services/app.service';
import { PaginationService } from '../../services/paginator.service';

const CANCEL_DEFAULT_REASON = '19';

@Component({
    selector: 'app-overview-table',
    templateUrl: './overview-table.component.html',
    styleUrls: ['./overview-table.component.scss'],
})
export class OverviewTableComponent implements OnInit, AfterViewInit, OnDestroy {
    constructor(
        @Inject(DOCUMENT) document: Document,
        private translationService: TranslateService,
        private permissionService: PermissionService,
        private selectedCustomerService: SelectedCustomerService,
        private dialog: MatDialog,
        private dataService: OverviewDataService,
        private appService: AppService,
        public paginatorService: PaginationService
    ) {
        this.helper = new OverlayDialogHelper<PriceSummaryDialogComponent, PriceSummaryDialogData>(
            document,
            this.dialog,
            PriceSummaryDialogComponent,
            'price-summary-dialog'
        );
        this.viewer = new OverlayDialogHelper<ImageDialogComponent, ImageDialogData>(
            document,
            dialog,
            ImageDialogComponent,
            'image-dialog'
        );
    }

    public get isPartnerMode() {
        return !!this.selectedCustomerService.customerForPartnerMode$?.value;
    }
    private unsubscribe$: Subject<void> = new Subject<void>();
    private helper: OverlayDialogHelper<PriceSummaryDialogComponent, PriceSummaryDialogData>;
    private viewer: OverlayDialogHelper<ImageDialogComponent, ImageDialogData>;
    private today: moment.Moment;

    @ViewChild(MatSort, { static: true })
    public sort: MatSort;

    @Input()
    public dataSource: OverviewDataSource<any, any>;
    @Input()
    public isEmployee: boolean;
    @Input() defaultSort = 'createdDate_desc';

    @Output()
    public viewOffer = new EventEmitter<any>();
    @Output()
    public copyOffer = new EventEmitter<any>();
    @Output()
    public deleteOffer = new EventEmitter<any>();
    @Output()
    public pagingError = new EventEmitter<any>();
    @Output()
    public sortError = new EventEmitter<any>();

    public columnsToDisplayForCustomer = [
        'status',
        'createdDate',
        'schemePreview',
        'title',
        'totalGross',
        'creatorFullName',
        'status',
        'actions',
    ];
    public columnsToDisplayForEmployee = [
        'status',
        'documentId',
        'title',
        'schemePreview',
        'createdDate',
        'creator',
        'totalGross',
        'actions',
    ];
    public columnsToDisplay;

    @ViewChild(MatPaginator, { static: true })
    public paginator: MatPaginator;

    public ngOnInit() {
        this.today = moment.utc().startOf('day');
        this.setColumnsToDisplay();
        this.dataSource.init(this.paginator);
        this.selectedCustomerService.customerForPartnerMode$.subscribe(() => this.setColumnsToDisplay());
        this.appService.salesOrg$.subscribe(() => this.setColumnsToDisplay());
        this.dataSource.loadOverviewData({
            parameters: {
                sortBy: 'createdDate',
                sortOrder: 'desc',
            },
        });
    }

    private setColumnsToDisplay() {
        this.columnsToDisplay = [
            ...(this.permissionService.isUserAnEmployee && !this.isPartnerMode
                ? this.columnsToDisplayForEmployee
                : this.columnsToDisplayForCustomer),
        ];
    }

    public ngAfterViewInit(): void {
        // listen to paging events
        this.paginator.page
            .pipe(
                mergeMap(() => this.dataSource.loadOverviewData()),
                catchError(() => {
                    this.pagingError.emit();
                    return EMPTY;
                })
            )
            .subscribe();
    }

    public onViewClick(quote) {
        this.viewOffer.emit(quote);
    }

    public onCopyClick(quote) {
        openQuoteCreationDialog(this.dialog, {
            title: this.translationService.instant('OVERVIEW.DIALOG.COPY_OFFER.TITLE'),
            confirm: this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            cancel: this.translationService.instant('COMMON.DIALOG.CANCEL'),
            quoteTitle: this.translationService.instant('OVERVIEW.DIALOG.COPY_OFFER.COPY') + ` ${quote.title}`,
        }).subscribe((title) => {
            if (title) {
                this.copyOffer.emit({
                    ...quote,
                    title,
                });
            }
        });
    }

    public onDeleteClick(quote) {
        let reasons$: Subject<{ [key: string]: string }>;
        if (this.isEmployee && quote.status.toLowerCase() !== 'new') {
            reasons$ = new Subject<{ [key: string]: string }>();
            this.dataService.getAvailableCancellationReasons().pipe(takeUntil(this.unsubscribe$)).subscribe(reasons$);
        }

        openQuoteDeleteDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.TITLE'),
            `${this.translationService.instant('OVERVIEW.DIALOG.DELETE_OFFER.MESSAGE')} ${quote.title}`,
            this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL'),
            reasons$
        ).subscribe((reason) => {
            reason = reason === true ? CANCEL_DEFAULT_REASON : reason;
            if (reason) {
                this.deleteOffer.emit({
                    ...quote,
                    reason,
                });
            }
        });
    }

    public getCreatorFullName(obj) {
        return obj.authorName ? `${obj.authorName.firstName || ''} ${obj.authorName.familyName || ''}` : '';
    }

    public handlePriceSummaryEvent(event: MouseEvent, obj) {
        if (obj.totalGross || obj.totalDiscount || obj.totalPrice) {
            this.helper.handleEvent(event, obj);
        }
    }

    public getStatus(item: ConfigitQuote) {
        return moment.utc(item.validTo).startOf('day').add(1, 'day').isSameOrBefore(this.today) &&
            item.status === 'Quoted'
            ? 'EXPIRED'
            : item.status.toUpperCase();
    }

    public onImageHover(event: MouseEvent, obj) {
        if (obj.status.toLowerCase() !== 'new') {
            this.viewer.handleEvent(event, {
                quoteId: obj.documentId,
                imageUrl: obj.schemePreview,
            });
        }
    }

    getTotalPages(): number {
        return this.paginatorService.getTotalPages(this.paginator);
    }

    goToFirstPage() {
        this.paginatorService.goToFirstPage(this.paginator);
    }

    goToPreviousPage() {
        this.paginatorService.goToPreviousPage(this.paginator);
    }

    goToNextPage() {
        this.paginatorService.goToNextPage(this.paginator);
    }

    goToLastPage() {
        this.paginatorService.goToLastPage(this.paginator);
    }

    public ngOnDestroy() {
        this.helper.destroy();
    }
}
